import { BOOK_PLAN_IDS, BOOK_PLANS, type BookPlanDefinition } from '@kitup/book-schema';
import {
  formatPrice,
  type BrandConfig,
  type LegalConfig,
  type StorefrontPageProps,
} from '@kitup/brand';
import type { JSX } from 'react';
import splitLayout from './assets/split.png';
import fullBleedLayout from './assets/full-bleed.png';
import bandLayout from './assets/band.png';
import showcasePoster from './assets/spread-1.webp';
import showcaseVideo from './assets/spread-1.mp4';
import roomReference from './assets/room.jpg';
import boyReference from './assets/boy.jpg';
import elkReference from './assets/elk.jpg';
import girlReference from './assets/girl.jpg';
import rabbitReference from './assets/rabit.jpg';
import momReference from './assets/mom.jpg';
import { storefrontCopy, type StorefrontCopy } from './copy.js';

/** Spread samples follow the order of the captions in the copy. */
const SAMPLE_IMAGES = [splitLayout, fullBleedLayout, bandLayout] as const;
const SHOWCASE_REFERENCES = [
  { src: boyReference, position: 'left-top' },
  { src: elkReference, position: 'left-middle' },
  { src: roomReference, position: 'left-bottom' },
  { src: girlReference, position: 'right-top' },
  { src: rabbitReference, position: 'right-middle' },
  { src: momReference, position: 'right-bottom' },
] as const;

/**
 * `/`. The steps and the samples are iterated from the copy: the layout knows
 * neither how many there are nor what they say.
 */
export function LandingPage({
  brand,
  legal,
  page,
}: {
  readonly brand: BrandConfig;
  readonly legal: LegalConfig;
  readonly page: StorefrontPageProps;
}): JSX.Element {
  const { locale, user, authPending, onSignOut, Link, footer } = page;
  const copy = storefrontCopy(brand, legal, locale);
  const { landing } = copy;
  const createBookTarget = user ? '/app' : '/register';

  return (
    <div className="kitup-landing">
      <a href="#landing-content" className="kitup-skip-link">
        {landing.skipToContent}
      </a>

      <header className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-4">
        <span className="text-lg font-semibold">{copy.name}</span>
        <nav aria-label={landing.navLabel} className="flex items-center gap-2">
          <Link to="/pricing" className="kitup-quiet-button">
            {copy.pricing.nav}
          </Link>
          {authPending ? null : user ? (
            <>
              <span className="px-2 text-sm text-[var(--kitup-color-muted)]">{user.name}</span>
              <button type="button" className="kitup-quiet-button" onClick={onSignOut}>
                {copy.auth.signOut}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="kitup-quiet-button">
                {copy.auth.signIn}
              </Link>
              <Link to="/register" className="kitup-secondary-button">
                {copy.auth.signUp}
              </Link>
            </>
          )}
        </nav>
      </header>

      <main id="landing-content">
        <section className="kitup-landing-hero">
          <div className="mx-auto grid max-w-3xl gap-5 px-4 py-16 text-center sm:py-24">
            <p className="text-sm tracking-wide text-[var(--kitup-color-muted)] uppercase">
              {landing.hero.eyebrow}
            </p>
            <h1 className="text-3xl leading-tight font-semibold text-balance sm:text-5xl">
              {landing.hero.title}
            </h1>
            <p className="mx-auto max-w-prose text-lg text-[var(--kitup-color-muted)]">
              {landing.hero.lead}
            </p>
            <div className="mt-2 flex flex-wrap justify-center gap-3">
              <Link to={createBookTarget} className="kitup-primary-button">
                {landing.hero.cta}
              </Link>
              <Link to={brand.landingExamplePath} className="kitup-secondary-button">
                {landing.hero.secondaryCta}
              </Link>
            </div>
            <p className="text-sm text-[var(--kitup-color-muted)]">{landing.hero.note}</p>
          </div>

          <figure className="kitup-landing-showcase">
            {SHOWCASE_REFERENCES.map((reference) => (
              <img
                key={reference.position}
                className={`kitup-landing-reference kitup-landing-reference-${reference.position}`}
                src={reference.src}
                alt=""
                loading="lazy"
                decoding="async"
              />
            ))}
            <div className="kitup-landing-showcase-frame">
              <video
                className="kitup-landing-showcase-video"
                aria-label={landing.showcase.videoLabel}
                poster={showcasePoster}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
              >
                <source src={showcaseVideo} type="video/mp4" />
              </video>
            </div>
            <figcaption className="sr-only">{landing.showcase.caption}</figcaption>
          </figure>
        </section>

        <section aria-labelledby="landing-steps" className="mx-auto max-w-5xl px-4 py-12">
          <h2 id="landing-steps" className="text-2xl font-semibold">
            {landing.steps.title}
          </h2>
          <ol className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {landing.steps.items.map((step, index) => (
              <li key={step.title} className="kitup-card grid gap-2 p-5">
                <span className="kitup-step-number" aria-hidden="true">
                  {(index + 1).toLocaleString(locale)}
                </span>
                <h3 className="font-semibold">{step.title}</h3>
                <p className="text-sm text-[var(--kitup-color-muted)]">{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section aria-labelledby="landing-samples" className="mx-auto max-w-5xl px-4 py-12">
          <h2 id="landing-samples" className="text-2xl font-semibold">
            {landing.samples.title}
          </h2>
          <p className="mt-2 max-w-prose text-sm text-[var(--kitup-color-muted)]">
            {landing.samples.hint}
          </p>
          <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {landing.samples.items.map((sample, index) => (
              <li key={sample.title}>
                <figure className="kitup-card grid gap-3 overflow-hidden p-3">
                  <img
                    className="kitup-landing-sample"
                    src={SAMPLE_IMAGES[index % SAMPLE_IMAGES.length] ?? splitLayout}
                    alt=""
                    loading="lazy"
                    decoding="async"
                  />
                  <figcaption className="grid gap-1 px-1 pb-1">
                    <span className="font-semibold">{sample.title}</span>
                    <span className="text-sm text-[var(--kitup-color-muted)]">
                      {sample.caption}
                    </span>
                  </figcaption>
                </figure>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="landing-final" className="mx-auto max-w-5xl px-4 py-12">
          <div className="kitup-card grid justify-items-center gap-3 p-8 text-center">
            <h2 id="landing-final" className="text-2xl font-semibold">
              {landing.final.title}
            </h2>
            <p className="max-w-prose text-[var(--kitup-color-muted)]">{landing.final.text}</p>
            <Link to={createBookTarget} className="kitup-primary-button mt-2">
              {landing.final.cta}
            </Link>
          </div>
        </section>
      </main>

      <footer className="mx-auto grid max-w-5xl justify-items-center gap-3 px-4 pt-4 pb-10 text-center [&>*]:justify-center text-sm text-[var(--kitup-color-muted)]">
        {footer}
      </footer>
    </div>
  );
}

interface PlanFeature {
  readonly text: string;
  readonly included: boolean;
}

/** The same comparison the book builder shows, in the storefront's own copy. */
function planFeatures(plan: BookPlanDefinition, copy: StorefrontCopy['features']): PlanFeature[] {
  const pageCount = plan.sceneCount * 2 + 2;

  return [
    { text: copy.pages(pageCount), included: true },
    { text: copy.characters(plan.maxCharacters), included: true },
    { text: copy.props(plan.maxThings, plan.maxLocations), included: true },
    { text: copy.resolution(plan.imageResolution.toUpperCase()), included: true },
    plan.video.enabled
      ? { text: copy.video(plan.video.resolution), included: true }
      : { text: copy.noVideo, included: false },
    plan.narration
      ? { text: copy.narration, included: true }
      : { text: copy.noNarration, included: false },
    { text: copy.share, included: true },
    plan.printReady
      ? {
          text: plan.printQuality === 'high' ? copy.printHigh : copy.printStandard,
          included: true,
        }
      : { text: copy.noPrint, included: false },
    { text: copy.replacements(plan.freeImageReplacements, plan.video.enabled), included: true },
  ];
}

/** `/pricing` */
export function PricingPage({
  brand,
  legal,
  page,
}: {
  readonly brand: BrandConfig;
  readonly legal: LegalConfig;
  readonly page: StorefrontPageProps;
}): JSX.Element {
  const { locale, user, Link, footer } = page;
  const copy = storefrontCopy(brand, legal, locale);
  const { pricing } = copy;
  const createBookTarget = user ? '/app' : '/register';

  return (
    <div className="kitup-landing min-h-screen">
      <header className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-4">
        <Link to="/" className="text-lg font-semibold">
          {copy.name}
        </Link>
        <nav aria-label={pricing.nav} className="flex items-center gap-2">
          <Link to="/" className="kitup-quiet-button">
            {pricing.home}
          </Link>
          <Link to={createBookTarget} className="kitup-primary-button">
            {pricing.cta}
          </Link>
        </nav>
      </header>

      <main className="mx-auto max-w-5xl px-4 pt-12 pb-16 sm:pt-16">
        <section className="text-center">
          <p className="text-sm tracking-wide text-[var(--kitup-color-muted)] uppercase">
            {pricing.oneTime}
          </p>
          <h1 className="mt-3 text-3xl font-semibold sm:text-5xl">{pricing.title}</h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-[var(--kitup-color-muted)]">
            {pricing.lead}
          </p>
        </section>

        <section className="kitup-plan-grid mt-10" aria-label={pricing.title}>
          {BOOK_PLAN_IDS.map((id) => {
            const plan = BOOK_PLANS[id];
            const planCopy = copy.plans[id];
            const headingId = `pricing-plan-${id}`;

            return (
              <article
                key={id}
                className="kitup-plan-card"
                data-popular={id === 'live_book' ? 'true' : 'false'}
                aria-labelledby={headingId}
              >
                <span className="kitup-plan-flag">
                  {id === 'live_book' ? (
                    <span className="kitup-plan-badge">{pricing.popular}</span>
                  ) : null}
                </span>
                <h2 id={headingId} className="kitup-plan-name">
                  {planCopy.name}
                </h2>
                <p className="kitup-plan-price">
                  {formatPrice(brand.planPrices[id], brand, locale)}
                </p>
                <p className="min-h-12 text-sm text-[var(--kitup-color-muted)]">
                  {planCopy.summary}
                </p>
                <ul className="kitup-plan-features">
                  {planFeatures(plan, copy.features).map((feature) => (
                    <li
                      key={feature.text}
                      className="kitup-plan-feature"
                      data-included={feature.included ? 'true' : 'false'}
                    >
                      {feature.text}
                    </li>
                  ))}
                </ul>
                <Link className="kitup-primary-button mt-4 justify-center" to={createBookTarget}>
                  {pricing.cta}
                </Link>
              </article>
            );
          })}
        </section>

        <section className="kitup-card mx-auto mt-10 grid max-w-3xl gap-3 p-6 text-center">
          <h2 className="text-xl font-semibold">{pricing.freePreview}</h2>
          <p className="text-sm text-[var(--kitup-color-muted)]">{pricing.delivery}</p>
          <p className="text-sm text-[var(--kitup-color-muted)]">{pricing.taxes}</p>
          <p className="text-sm">
            {pricing.legalPrefix}{' '}
            <Link className="underline" to={legal.routes.payment}>
              {pricing.legalLink}
            </Link>
            .
          </p>
        </section>
      </main>

      <footer className="mx-auto grid max-w-5xl justify-items-center gap-3 px-4 pt-4 pb-10 text-center [&>*]:justify-center text-sm text-[var(--kitup-color-muted)]">
        {footer}
      </footer>
    </div>
  );
}
